import {
  chartLegendEntry,
  escapeHtml,
  missingDataMode,
  NO_DATA_LABEL,
  periodBounds,
  PIRACY_AUDIENCE_SERIES_CONTRACT,
  positionChartTooltip,
  prefersReducedMotion,
  timeGaps,
  timeNoDataAreas,
  useChartAnimation,
  useChartLifecycle,
  type BlockingInterval,
  type MissingDataMode,
  type PiracyAudienceReportMetrics,
  type PiracyAudienceSeriesData,
  type ReportMetric,
} from "@nexus/widget-kit";
export { escapeHtml } from "@nexus/widget-kit";
import { useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  balancedRows,
  shownSummaryFigures,
  summaryMetricIds,
  type SummaryFigure,
} from "./summaryFigures";
import {
  cn,
  createDateFormatter,
  displayDate,
  formatDisplayDay,
  InfoTip,
  useDisplayTimezone,
  type TimezoneMode,
} from "@nexus/ui";
import * as echarts from "echarts/core";
import { LineChart, type LineSeriesOption } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TooltipComponent,
  type DataZoomComponentOption,
  type GridComponentOption,
  type LegendComponentOption,
  type MarkAreaComponentOption,
  type MarkLineComponentOption,
  type MarkPointComponentOption,
  type TooltipComponentOption,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  defineLabels,
  firstError,
  HeaderStats,
  resolveChartColors,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  useHeaderSummaryShown,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  WidgetState,
  MetricStat,
  withAlpha,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type {
  ChartColors,
  WidgetDataRequirements,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";

echarts.use([
  LineChart,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type PiracyAudienceOption = echarts.ComposeOption<
  | LineSeriesOption
  | GridComponentOption
  | LegendComponentOption
  | TooltipComponentOption
  | MarkAreaComponentOption
  | MarkLineComponentOption
  | MarkPointComponentOption
  | DataZoomComponentOption
>;

export interface PiracyAudienceTrendConfig {
  title?: string;
  audienceDisplay?: "split" | "line";
  showObserved?: boolean;
  showEstimated?: boolean;
  /** `false` hides the blocking period bands and their spotlight. Default `true`. */
  showBlocking?: boolean;
  /** Show gap (default) keeps the whole period on the axis; Fit to data crops it to the samples. */
  missingData?: MissingDataMode;
  /** Per-instance overrides of the texts listed in `manifest.labels`. */
  labels?: Record<string, string>;
  /** Adds the summary strip and data information in the header. */
  showDetails?: boolean;
  summaryPosition?: "top" | "left" | "right";
  /** Each figure of the summary on or off (see `SUMMARY_FIGURES` for which are on by default). */
  summaryShowLatest?: boolean;
  summaryShowPeak?: boolean;
  summaryShowAverage?: boolean;
  summaryShowMax?: boolean;
  summaryShowDisrupted?: boolean;
  summaryShowSafe?: boolean;
  summaryShowBlocking?: boolean;
  /**
   * How a period spotlighted by a linked Blocking periods widget shows: `focus`
   * (default) dims the rest of the chart and marks the period's peak; `band`
   * only emphasizes its band. Without a linked spotlight nothing is highlighted.
   */
  spotlightEffect?: SpotlightEffect;
  /** `true` sweeps the audience in, then the bands and counterfactual; off by default (drawn at once). */
  animateChart?: boolean;
}

export type SpotlightEffect = "focus" | "band";

export const SPOTLIGHT_EFFECTS: readonly { value: SpotlightEffect; label: string }[] = [
  { value: "focus", label: "Focus" },
  { value: "band", label: "Band only" },
];

export interface AudienceOptionSettings {
  timezone?: TimezoneMode;
  showLegend?: boolean;
  audienceDisplay?: "split" | "line";
  showObserved?: boolean;
  showEstimated?: boolean;
  showBlocking?: boolean;
  missingData?: MissingDataMode;
  labels?: AudienceLabels;
  activePeriodId?: string | null;
  spotlightEffect?: SpotlightEffect;
  /** `false` draws without animation, e.g. on the editor canvas. Reduced motion always does. */
  animate?: boolean;
  /**
   * The legend's on/off state by series name (ECharts `legend.selected`), kept
   * across redraws. Hiding Unsafe to Block draws Safe to Block from zero.
   */
  legendSelected?: Record<string, boolean>;
  /**
   * A short plot (a fitted wall screen, a compact row): the vertical axis title
   * is left out, as it would be longer than the axis itself, and the plot moves up.
   */
  short?: boolean;
  /**
   * The first, animated draw: the annotations (blocking bands, tint, focus veil,
   * peak marker) are left out and the counterfactual is transparent, so the
   * audience sweeps in alone. The host draws again with them once that first
   * animation has finished (at the latest after `SECONDARY_REVEAL_MS`); the
   * counterfactual keeps its place in the scale meanwhile, so nothing moves when
   * it appears.
   */
  deferSecondary?: boolean;
}

/**
 * The audience sweeps in for `AUDIENCE_DRAW_MS`; annotations and secondary lines
 * appear when it has finished, or after `SECONDARY_REVEAL_MS` at the latest.
 */
export const AUDIENCE_DRAW_MS = 600;
export const SECONDARY_REVEAL_MS = AUDIENCE_DRAW_MS + 400;

/** Below this plot height (px) the chart switches to its short layout. */
export const SHORT_CHART_HEIGHT = 280;

export const LABELS = defineLabels([
  { key: "audience", name: "Audience series", default: "Pirate audience" },
  { key: "safe", name: "Safe series", default: "Safe to Block" },
  { key: "unsafe", name: "Unsafe series", default: "Unsafe to Block" },
  {
    key: "observed",
    name: "Observed traffic series",
    default: "Observed traffic",
    hint: "The observed traffic line, e.g. the name of the monitored feed.",
  },
  {
    key: "observedScope",
    name: "Observed platform scope (tooltip)",
    default: "Observed CDN traffic; all platforms",
    advanced: true,
  },
  {
    key: "estimated",
    name: "Counterfactual series",
    default: "Counterfactual traffic",
    hint: "Shown only when the data includes a counterfactual estimate.",
  },
  {
    key: "estimatedScope",
    name: "Counterfactual platform scope (tooltip)",
    default: "Source model estimate for the matched platform group",
    advanced: true,
  },
  {
    key: "blocking",
    name: "Blocking period",
    default: "Blocking period",
    advanced: true,
    hint: "Shown in the tooltip when the hour falls in a blocking period.",
  },
  { key: "yAxis", name: "Value axis title", default: "Approximated pirate audience" },
  { key: "noSample", name: "Missing sample (tooltip)", default: "No data" },
  { key: "resetZoom", name: "Reset zoom button", default: "Reset zoom" },
  {
    key: "summaryLatest",
    name: "Summary: latest audience",
    default: "Latest audience",
    advanced: true,
  },
  { key: "summaryPeak", name: "Summary: peak", default: "Peak", advanced: true },
  {
    key: "summaryAverage",
    name: "Summary: average viewers",
    default: "Average viewers",
    advanced: true,
  },
  { key: "summaryMax", name: "Summary: max viewers", default: "Max viewers", advanced: true },
  {
    key: "summaryDisrupted",
    name: "Summary: viewers disrupted",
    default: "Viewers disrupted",
    advanced: true,
  },
  { key: "summarySafe", name: "Summary: Safe ratio", default: "Safe to block", advanced: true },
  { key: "summaryBlocking", name: "Summary: blocking status", default: "Blocking", advanced: true },
  {
    key: "reportAvgApprox",
    name: "Report metric: average approximate audience",
    default: "Avg. approx. audience",
    advanced: true,
  },
  {
    key: "reportAvgEstimated",
    name: "Report metric: average counterfactual audience",
    default: "Avg. counterfactual audience",
    advanced: true,
  },
  {
    key: "reportReduction",
    name: "Report metric: audience reduction",
    default: "Audience reduction",
    advanced: true,
  },
  {
    key: "reportRevenue",
    name: "Report metric: revenue opportunity",
    default: "Revenue opportunity",
    advanced: true,
  },
  { key: "emptyTitle", name: "Empty title", default: "No audience data" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "No audience samples for this period.",
  },
] as const);

export type AudienceLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;
const DEFAULT_LABELS: AudienceLabels = resolveLabels(LABELS, undefined);

export const manifest: WidgetManifest = {
  id: "piracy-audience-trend",
  name: "Piracy Audience Trend",
  description:
    "Piracy audience split by block safety, with the observed traffic and the periods during which blocking ran. Renders an optional counterfactual series and business-impact metrics when the source provides them, so the same widget serves both a live War Room view and a closed report's audience section.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    series: {
      source: "piracy-audience",
      contract: PIRACY_AUDIENCE_SERIES_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: PiracyAudienceTrendConfig = {
  title: "Audience on pirate services vs blocking periods",
  showDetails: false,
  summaryPosition: "top",
  audienceDisplay: "split",
  showObserved: true,
  showEstimated: true,
  showBlocking: true,
};

export const SAFE_SERIES = DEFAULT_LABELS.safe;
export const UNSAFE_SERIES = DEFAULT_LABELS.unsafe;
export const OBSERVED_SERIES = DEFAULT_LABELS.observed;
export const BLOCKING_SERIES = DEFAULT_LABELS.blocking;

const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };

// ── Formatting ────────────────────────────────────────────────────────────────

const countFormat = new Intl.NumberFormat("en");
const compactFormat = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const currencyFormat = new Intl.NumberFormat("en", {
  style: "currency",
  currency: "EUR",
  notation: "compact",
  maximumFractionDigits: 1,
});

export function formatCount(value: number): string {
  return countFormat.format(value);
}

function toMs(value: string): number | null {
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function formatMoment(ms: number, timezone: TimezoneMode = "utc"): string {
  return createDateFormatter(timezone, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(ms);
}

export function formatStatus(status: string): string {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown";
}

// ── Derived facts ─────────────────────────────────────────────────────────────

export interface AudienceSummary {
  latest: { timestamp: string; total: number; safeShare: number | null } | null;
  peak: { timestamp: string; total: number } | null;
  activeInterval: BlockingInterval | null;
  intervalCount: number;
}

export function deriveAudienceSummary(data: PiracyAudienceSeriesData): AudienceSummary {
  let latest: AudienceSummary["latest"] = null;
  let peak: AudienceSummary["peak"] = null;
  let latestMs = -Infinity;
  for (const point of data.points) {
    const ms = toMs(point.timestamp);
    if (ms === null) continue;
    const total = point.safeToBlock + point.unsafeToBlock;
    if (ms > latestMs) {
      latestMs = ms;
      latest = {
        timestamp: point.timestamp,
        total,
        safeShare: total > 0 ? point.safeToBlock / total : null,
      };
    }
    if (!peak || total > peak.total) peak = { timestamp: point.timestamp, total };
  }
  return {
    latest,
    peak,
    activeInterval: data.blockingIntervals.find((interval) => interval.status === "active") ?? null,
    intervalCount: data.blockingIntervals.length,
  };
}

/** The blocking interval covering an instant, if any (start inclusive, end exclusive). */
export function findIntervalAt(
  intervals: readonly BlockingInterval[],
  ms: number,
): BlockingInterval | null {
  return (
    intervals.find((interval) => {
      const from = toMs(interval.from);
      const to = toMs(interval.to);
      return from !== null && to !== null && ms >= from && ms < to;
    }) ?? null
  );
}

export function describeAudienceChart(
  summary: AudienceSummary,
  options?: AudienceOptionSettings,
): string {
  if (!summary.latest || !summary.peak) return "No audience samples.";
  const peak = `Peak ${formatCount(summary.peak.total)} viewers on ${formatMoment(Date.parse(summary.peak.timestamp), options?.timezone)}`;
  const blocking =
    summary.intervalCount === 0
      ? "no blocking period"
      : `${summary.intervalCount} blocking ${summary.intervalCount === 1 ? "period" : "periods"}${summary.activeInterval ? ", one active" : ""}`;
  const display =
    options?.audienceDisplay === "line" ? "total audience" : "split into safe and unsafe to block";
  const reference = options?.showObserved === false ? "" : ", with observed traffic";
  return `Piracy audience samples, ${display}${reference}. ${peak}; ${blocking}.`;
}

// ── Chart option ──────────────────────────────────────────────────────────────

type SeriesPoint = [number, number | null];

/** PAM samples every five minutes. */
const SAMPLE_STEP_MS = 5 * 60 * 1000;

/** The sampling step: the most common interval between samples, five minutes by default. */
export function sampleStep(times: readonly number[]): number {
  const counts = new Map<number, number>();
  for (let index = 1; index < times.length; index += 1) {
    const step = times[index] - times[index - 1];
    if (step > 0) counts.set(step, (counts.get(step) ?? 0) + 1);
  }
  let best = SAMPLE_STEP_MS;
  let bestCount = 0;
  for (const [step, count] of counts) if (count > bestCount) [best, bestCount] = [step, count];
  return best;
}

function toSeriesData(data: PiracyAudienceSeriesData) {
  const safe: SeriesPoint[] = [];
  const unsafe: SeriesPoint[] = [];
  const observed: SeriesPoint[] = [];
  const estimated: SeriesPoint[] = [];
  const audience: SeriesPoint[] = [];
  const spans = bandSpans(data);
  let previousSpan: (typeof spans)[number] | undefined;
  const points = data.points
    .map((point) => ({ point, ms: toMs(point.timestamp) }))
    .filter((entry): entry is { point: typeof entry.point; ms: number } => entry.ms !== null)
    .sort((a, b) => a.ms - b.ms);
  const times = points.map(({ ms }) => ms);
  const step = sampleStep(times);
  let previousMs: number | undefined;
  for (const { point, ms } of points) {
    // A hole longer than two samples is missing data: the curves stop there instead of bridging it.
    if (previousMs !== undefined && ms - previousMs > step * 2)
      for (const values of [safe, unsafe, observed, audience])
        values.push([previousMs + step, null]);
    previousMs = ms;
    safe.push([ms, point.safeToBlock]);
    unsafe.push([ms, point.unsafeToBlock]);
    observed.push([ms, point.observed]);
    audience.push([ms, point.safeToBlock + point.unsafeToBlock]);
    const span = spans.find((item) => ms >= item.from && ms < item.to);
    if (previousSpan && span !== previousSpan && previousSpan.to < ms) {
      estimated.push([previousSpan.to, null]);
    }
    estimated.push([
      ms,
      span && point.estimated != null && Number.isFinite(point.estimated) ? point.estimated : null,
    ]);
    previousSpan = span;
  }
  const hasEstimated = estimated.some(([, value]) => value !== null);
  const byMs = new Map(points.map(({ point, ms }) => [ms, point]));
  return {
    safe,
    unsafe,
    observed,
    audience,
    byMs,
    times,
    step,
    estimated: hasEstimated ? estimated : null,
  };
}

/** Suffix of the id of a line's casing, a wider stroke drawn under it. */
const CASING_SUFFIX = "-casing";

/** Opacity of the band fills: at rest, on show, and receding while another is on show. */
export const BAND_ALPHA = { rest: 0.3, active: 0.36, selected: 0.52, receded: 0.2 } as const;

/** Opacity of the veil laid over the chart outside the spotlighted period. */
export const FOCUS_VEIL_ALPHA = 0.5;

/** Green for blocking that reaches the scope on show, grey for periods that ran elsewhere. */
export function blockingColorOf(data: PiracyAudienceSeriesData, colors: ChartColors): string {
  return data.blockingApplies === false
    ? (colors.audience?.blockingInactive ?? colors.label)
    : (colors.audience?.blocking ?? colors.classification?.safe ?? colors.series[1]);
}

function bandSpans(data: PiracyAudienceSeriesData) {
  const spans = (data.blockingPeriods ?? data.blockingIntervals)
    .flatMap((interval) => {
      const from = toMs(interval.from);
      const to = toMs(interval.to);
      if (from === null || to === null || to <= from) return [];
      return [
        {
          from,
          to,
          active: interval.status === "active",
          id: "id" in interval ? interval.id : undefined,
        },
      ];
    })
    .sort((left, right) => left.from - right.from);
  const mergedSpans: typeof spans = [];
  for (const span of spans) {
    const previous = mergedSpans[mergedSpans.length - 1];
    if (data.blockingPeriods === undefined && previous && span.from <= previous.to) {
      previous.to = Math.max(previous.to, span.to);
      previous.active ||= span.active;
    } else {
      mergedSpans.push({ ...span });
    }
  }
  return mergedSpans;
}

/**
 * One band per blocking period. While the dashboard spotlights a period, its
 * band comes forward with a stronger fill, and every other
 * band recedes, so the period on show is the one the eye finds first (its peak
 * marker, see `buildPeakMarker`, names it). Without a (matching) selection all bands
 * share the resting treatment.
 */
export function buildBlockingBands(
  data: PiracyAudienceSeriesData,
  colors: ChartColors,
  activePeriodId?: string | null,
): NonNullable<MarkAreaComponentOption["data"]> {
  const blockingColor = blockingColorOf(data, colors);
  const spans = bandSpans(data);
  const spotlighting =
    activePeriodId != null &&
    spans.some((span) => span.id !== undefined && span.id === activePeriodId);
  return spans.map((span) => {
    const selected = spotlighting && span.id === activePeriodId;
    const alpha = bandAlpha(spotlighting, selected, span.active);
    return [
      {
        name: span.id,
        xAxis: span.from,
        label: { show: false },
        itemStyle: {
          color: withAlpha(blockingColor, alpha),
          borderWidth: 0,
        },
      },
      { xAxis: span.to },
    ];
  });
}

function bandAlpha(spotlighting: boolean, selected: boolean, active: boolean) {
  return selected
    ? BAND_ALPHA.selected
    : spotlighting
      ? BAND_ALPHA.receded
      : active
        ? BAND_ALPHA.active
        : BAND_ALPHA.rest;
}

/** Share of a band's opacity laid over the audience areas, so the period reads full height. */
export const BAND_TINT_SHARE = 0.4;

/**
 * The part of each blocking band drawn *over* the audience areas: a light tint
 * and a hairline at both ends. The full band (`buildBlockingBands`) lies under
 * the opaque areas, which hid its lower part: the period looked cut off at the
 * bottom wherever the audience was high. The tint keeps the areas' colours
 * readable; the edges say exactly where blocking starts and ends.
 */
export function buildBlockingTint(
  data: PiracyAudienceSeriesData,
  colors: ChartColors,
  activePeriodId?: string | null,
): {
  areas: NonNullable<MarkAreaComponentOption["data"]>;
  edges: NonNullable<MarkLineComponentOption["data"]>;
} {
  const blockingColor = blockingColorOf(data, colors);
  const spans = bandSpans(data);
  const spotlighting =
    activePeriodId != null &&
    spans.some((span) => span.id !== undefined && span.id === activePeriodId);
  const areas: NonNullable<MarkAreaComponentOption["data"]> = [];
  const edges: NonNullable<MarkLineComponentOption["data"]> = [];
  for (const span of spans) {
    const selected = spotlighting && span.id === activePeriodId;
    const alpha = bandAlpha(spotlighting, selected, span.active);
    areas.push([
      {
        name: span.id,
        xAxis: span.from,
        label: { show: false },
        itemStyle: { color: withAlpha(blockingColor, alpha * BAND_TINT_SHARE), borderWidth: 0 },
      },
      { xAxis: span.to },
    ]);
    const lineStyle = {
      color: withAlpha(blockingColor, Math.min(1, alpha * 2)),
      width: 1,
      type: "solid" as const,
    };
    edges.push({ xAxis: span.from, lineStyle }, { xAxis: span.to, lineStyle });
  }
  return { areas, edges };
}

/**
 * While a period is spotlighted, a veil of the widget surface dims the chart
 * before and after it, so the period reads like a lit stage: its curves keep
 * full contrast, the rest of the week stays readable but steps back. When the
 * dashboard moves to the next period the veil edges glide to it (ECharts update
 * animation). Empty without a matching, valid selection.
 */
export function buildFocusVeil(
  data: PiracyAudienceSeriesData,
  colors: ChartColors,
  activePeriodId?: string | null,
): NonNullable<MarkAreaComponentOption["data"]> {
  const period = activePeriodId
    ? data.blockingPeriods?.find((item) => item.id === activePeriodId)
    : undefined;
  const from = period ? toMs(period.from) : null;
  const to = period ? toMs(period.to) : null;
  const times = data.points.map((point) => toMs(point.timestamp)).filter((ms) => ms !== null);
  if (from === null || to === null || to <= from || times.length === 0) return [];
  const first = Math.min(...times, from);
  const last = Math.max(...times, to);
  const veil = {
    itemStyle: { color: withAlpha(colors.tooltipBg, FOCUS_VEIL_ALPHA), borderWidth: 0 },
  };
  // Both rectangles always exist (possibly empty) so an update morphs them instead of replacing them.
  return [
    [{ ...veil, xAxis: first }, { xAxis: from }],
    [{ ...veil, xAxis: to }, { xAxis: last }],
  ];
}

/**
 * The highest combined audience sampled inside the spotlighted period (start
 * inclusive, end exclusive), or `null` without a matching selection or sample.
 */
export function findPeriodPeak(
  data: PiracyAudienceSeriesData,
  activePeriodId?: string | null,
): { ms: number; total: number } | null {
  const period = activePeriodId
    ? data.blockingPeriods?.find((item) => item.id === activePeriodId)
    : undefined;
  const from = period ? toMs(period.from) : null;
  const to = period ? toMs(period.to) : null;
  if (from === null || to === null || to <= from) return null;
  let peak: { ms: number; total: number } | null = null;
  for (const point of data.points) {
    const ms = toMs(point.timestamp);
    if (ms === null || ms < from || ms >= to) continue;
    const total = point.safeToBlock + point.unsafeToBlock;
    if (!Number.isFinite(total)) continue;
    if (!peak || total > peak.total) peak = { ms, total };
  }
  return peak;
}

/**
 * A marker on the top of the curve at the spotlighted period's peak, labelled
 * with the period's day and that audience ("Sat 16 Aug · Peak 2,134").
 */
export function buildPeakMarker(
  data: PiracyAudienceSeriesData,
  colors: ChartColors,
  activePeriodId?: string | null,
  timezone: TimezoneMode = "utc",
): NonNullable<MarkPointComponentOption["data"]> {
  const peak = findPeriodPeak(data, activePeriodId);
  const period = data.blockingPeriods?.find((item) => item.id === activePeriodId);
  if (!peak || !period) return [];
  const blockingColor = blockingColorOf(data, colors);
  return [
    {
      name: period.id,
      coord: [peak.ms, peak.total],
      value: peak.total,
      symbol: "circle",
      symbolSize: 11,
      itemStyle: { color: blockingColor, borderColor: colors.tooltipBg, borderWidth: 2 },
      label: {
        show: true,
        position: "top",
        distance: 8,
        formatter: `${formatDisplayDay(Date.parse(period.from), timezone)} · Peak ${formatCount(peak.total)}`,
        color: colors.tooltipText,
        backgroundColor: colors.tooltipBg,
        borderRadius: 4,
        padding: [3, 6],
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: 600,
        ...fontBase,
      },
    },
  ];
}

export function buildOption(
  data: PiracyAudienceSeriesData,
  colors: ChartColors,
  options?: AudienceOptionSettings,
): PiracyAudienceOption {
  const timezone = options?.timezone ?? "utc";
  const dayOnly = createDateFormatter(timezone, { month: "short", day: "numeric" });
  const timeOnly = createDateFormatter(timezone, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const labels = options?.labels ?? DEFAULT_LABELS;
  const {
    safe,
    unsafe,
    observed,
    audience,
    byMs,
    times,
    step,
    estimated: availableEstimate,
  } = toSeriesData(data);
  // Show gap: the axis spans the requested period. Fit to data: it spans the samples only.
  const period = options?.missingData === "fit" ? null : periodBounds(data.period);
  const gaps = timeGaps(times, step, period);
  const span = period
    ? period.to - period.from
    : times.length
      ? times[times.length - 1] - times[0]
      : 0;
  const estimated = options?.showEstimated === false ? null : availableEstimate;
  const audienceLine = options?.audienceDisplay === "line";
  const showObserved = options?.showObserved ?? true;
  const safeColor = colors.audience?.safe ?? colors.series[0];
  const unsafeColor =
    colors.audience?.unsafe ?? colors.classification?.unsafe ?? colors.statusError;
  const observedColor = colors.audience?.traffic ?? colors.now;
  // Not the brand cyan: it sat on the Safe to Block blue and vanished there.
  const estimatedColor = colors.audience?.counterfactual ?? colors.accent;
  const plotColor = colors.audience?.plot ?? withAlpha(colors.grid, 0.6);
  const blockingColor = blockingColorOf(data, colors);
  // The shared chart label role: 12px on a workstation, the presentation label size on a wall.
  const labelSize = Math.max(12, colors.typography?.labelSize ?? 12);
  const showBlocking = options?.showBlocking ?? true;
  // First draw: the audience alone; bands, veil, marker and counterfactual come after.
  const animated = (options?.animate ?? true) && !prefersReducedMotion();
  const deferred = animated && options?.deferSecondary === true;
  const estimatedOpacity = deferred ? 0 : 1;
  const annotated = showBlocking && !deferred;
  const bands = annotated ? buildBlockingBands(data, colors, options?.activePeriodId) : [];
  const tint = annotated
    ? buildBlockingTint(data, colors, options?.activePeriodId)
    : { areas: [], edges: [] };
  const focus = annotated && (options?.spotlightEffect ?? "focus") === "focus";
  const unsafeShown = options?.legendSelected?.[labels.unsafe] !== false;

  // The split is drawn as two overlapping areas from zero, not as an ECharts
  // stack: the total in the Safe colour, the Unsafe part over it. Stacked areas
  // are two polygons sharing an edge; each antialiases that edge against the
  // plot behind it, so a dark hairline showed between them and thin Safe
  // slivers vanished. Overlapping, the Unsafe edge falls on Safe, never on the
  // plot. Hiding Unsafe from the legend draws Safe alone.
  // Every series has a stable id: a redraw (a resize, a legend toggle, a short
  // plot) updates it in place. Without one, `replaceMerge` dropped and re-created
  // the audience on each redraw — it vanished and swept in again, behind the
  // bands and the counterfactual, which kept their ids.
  const area = (
    id: string,
    name: string,
    color: string,
    values: SeriesPoint[],
    extra?: Partial<LineSeriesOption>,
  ): LineSeriesOption => ({
    id,
    name,
    type: "line",
    data: values,
    smooth: false,
    showSymbol: false,
    symbol: "none",
    clip: true,
    lineStyle: { width: 0, color },
    itemStyle: { color },
    areaStyle: { color, opacity: 1 },
    emphasis: { focus: "none" },
    z: 2,
    ...extra,
  });

  return {
    useUTC: timezone === "utc",
    animation: animated,
    // The audience sweeps in from the left on first draw; later updates (a new
    // spotlighted period, a refresh) glide rather than jump.
    animationDuration: AUDIENCE_DRAW_MS,
    animationEasing: "cubicOut",
    animationDurationUpdate: 450,
    animationEasingUpdate: "cubicInOut",
    backgroundColor: "transparent",
    grid: {
      top: 44,
      right: 24,
      bottom: 40,
      left: 64,
      outerBoundsMode: "none",
      show: true,
      backgroundColor: plotColor,
      borderWidth: 0,
    },
    legend: {
      show: options?.showLegend ?? true,
      top: 8,
      right: 24,
      // The blocking bands are explained by the widget title, not by a legend entry.
      data: [
        ...(estimated ? [chartLegendEntry(labels.estimated, "dashed")] : []),
        ...(showObserved ? [chartLegendEntry(labels.observed, "line")] : []),
        ...(audienceLine
          ? [chartLegendEntry(labels.audience, "line")]
          : [chartLegendEntry(labels.safe, "rect"), chartLegendEntry(labels.unsafe, "rect")]),
      ],
      ...(options?.legendSelected ? { selected: options.legendSelected } : {}),
      itemWidth: 14,
      itemHeight: 14,
      itemGap: 16,
      type: "scroll",
      textStyle: { color: colors.tooltipText, fontSize: labelSize, ...fontBase },
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: {
        color: colors.tooltipText,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      axisPointer: {
        type: "line",
        lineStyle: { color: withAlpha(colors.label, 0.3), width: 1 },
      },
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesId?: string;
          seriesName?: string;
          marker?: string;
          axisValue?: number;
          value?: [number, number | null];
        }>;
        const ms = items[0]?.axisValue ?? items[0]?.value?.[0];
        if (typeof ms !== "number") return "";
        const point = byMs.get(ms);
        // Safe to Block is drawn as the total: the tooltip reads the sample itself.
        const sampled = (name: string, value: number | null | undefined) =>
          name === labels.safe && point ? point.safeToBlock : value;
        const order = [
          labels.unsafe,
          labels.safe,
          labels.audience,
          labels.observed,
          labels.estimated,
        ];
        const rows = items
          .filter((item) => item.seriesName && item.seriesName !== labels.blocking)
          .filter((item) => !item.seriesId?.endsWith(CASING_SUFFIX))
          .filter(
            (item) => item.seriesName !== labels.estimated || typeof item.value?.[1] === "number",
          )
          .sort((a, b) => order.indexOf(a.seriesName ?? "") - order.indexOf(b.seriesName ?? ""))
          .map((item) => {
            const value = sampled(item.seriesName ?? "", item.value?.[1]);
            return `<div style="display:flex;justify-content:space-between;gap:16px"><span>${item.marker ?? ""}${escapeHtml(item.seriesName ?? "")}</span><b>${typeof value === "number" ? formatCount(value) : escapeHtml(labels.noSample)}</b></div>`;
          })
          .join("");
        const valueOf = (name: string) => {
          const value = sampled(name, items.find((item) => item.seriesName === name)?.value?.[1]);
          return typeof value === "number" ? value : null;
        };
        const safeValue = valueOf(labels.safe);
        const unsafeValue = valueOf(labels.unsafe);
        const estimateScope =
          valueOf(labels.estimated) !== null
            ? `<div style="margin-top:4px;max-width:32ch;white-space:normal">${escapeHtml(labels.estimatedScope)}</div>`
            : "";
        const observedScope =
          valueOf(labels.observed) !== null
            ? `<div style="margin-top:4px;max-width:32ch;white-space:normal">${escapeHtml(labels.observedScope)}</div>`
            : "";
        const total =
          safeValue !== null || unsafeValue !== null ? (safeValue ?? 0) + (unsafeValue ?? 0) : null;
        // Same closing rows as the Blocking Trend tooltip, so side-by-side screens read alike.
        const totals =
          !audienceLine && total !== null
            ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${withAlpha(colors.label, 0.25)}"><div style="display:flex;justify-content:space-between;gap:16px"><span>Pirate audience</span><b>${formatCount(total)}</b></div>${safeValue !== null && total > 0 ? `<div style="display:flex;justify-content:space-between;gap:16px"><span>Safe ratio</span><b>${Math.round((safeValue / total) * 100)}%</b></div>` : ""}</div>`
            : "";
        const interval = showBlocking ? findIntervalAt(data.blockingIntervals, ms) : null;
        const blockingLabel =
          data.blockingApplies === false ? "Blocking in other countries" : "Blocking";
        const blocking = interval
          ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${withAlpha(colors.label, 0.25)}">${blockingLabel} · ${escapeHtml(formatStatus(interval.status))}${interval.provider ? ` · ${escapeHtml(interval.provider)}` : ""}</div>`
          : "";
        return `<div style="margin-bottom:4px;opacity:0.75">${escapeHtml(formatMoment(ms, timezone))}</div>${rows}${observedScope}${estimateScope}${totals}${blocking}`;
      },
    },
    // filterMode "none" keeps every point in the model so smooth curves never kink
    // at the viewport edge; series are clipped at the grid instead (same as event-evolution).
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        minSpan: 3,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
      },
    ],
    xAxis: {
      type: "time",
      min: period?.from ?? "dataMin",
      max: period?.to ?? "dataMax",
      maxInterval: 24 * 60 * 60 * 1000,
      axisLabel: {
        color: colors.tooltipText,
        hideOverlap: true,
        showMinLabel: true,
        margin: 10,
        fontSize: labelSize,
        ...fontBase,
        // Midnight shows the date, any other tick the time: it stays readable once zoomed.
        formatter: (value: number, index: number) =>
          index === 0 ||
          (displayDate(new Date(value), timezone).getUTCHours() === 0 &&
            displayDate(new Date(value), timezone).getUTCMinutes() === 0)
            ? dayOnly.format(new Date(value))
            : timeOnly.format(new Date(value)),
      },
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      min: 0,
      ...(estimated
        ? {
            max: ({ max }: { max: number }) => {
              const step = 10 ** Math.max(0, Math.floor(Math.log10(Math.max(1, max))) - 1);
              return Math.max(step, Math.ceil((max * 1.025) / step) * step);
            },
            splitNumber: 6,
          }
        : {}),
      name: "",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: colors.grid, type: "solid" } },
      axisLabel: {
        color: colors.tooltipText,
        fontSize: labelSize,
        formatter: (value: number) => compactFormat.format(value),
        ...fontBase,
      },
    },
    series: [
      ...(audienceLine
        ? [
            {
              id: "audience-line",
              name: labels.audience,
              type: "line" as const,
              data: audience,
              showSymbol: false,
              symbol: "none",
              connectNulls: false,
              smooth: false,
              lineStyle: { width: 2, color: safeColor },
              itemStyle: { color: safeColor },
              z: 5,
            },
          ]
        : [
            area("audience-safe", labels.safe, safeColor, unsafeShown ? audience : safe, {
              z: 2,
            }),
            area("audience-unsafe", labels.unsafe, unsafeColor, unsafe, { z: 3 }),
          ]),
      ...(showObserved
        ? [
            {
              id: "observed",
              name: labels.observed,
              type: "line" as const,
              data: observed,
              showSymbol: false,
              symbol: "none",
              connectNulls: false,
              smooth: false,
              lineStyle: { width: 2, color: observedColor },
              itemStyle: { color: observedColor },
              z: 5,
            },
          ]
        : []),
      {
        name: labels.blocking,
        id: "blocking-period-bands",
        type: "line",
        data: [],
        silent: true,
        itemStyle: { color: withAlpha(blockingColor, 0.75) },
        lineStyle: { color: withAlpha(blockingColor, 0.75) },
        markArea: { silent: true, data: bands, z: 1 },
        markPoint: {
          silent: true,
          data: focus ? buildPeakMarker(data, colors, options?.activePeriodId, timezone) : [],
          z: 7,
          animationDuration: 500,
          animationEasing: "backOut",
        },
      },
      {
        // Over the audience areas, under the traffic lines: the period reads full height.
        name: labels.blocking,
        id: "blocking-period-tint",
        type: "line",
        data: [],
        silent: true,
        z: 4,
        markArea: { silent: true, z: 4, data: tint.areas },
        markLine: {
          silent: true,
          z: 4,
          symbol: ["none", "none"],
          label: { show: false },
          emphasis: { disabled: true },
          animation: false,
          data: tint.edges,
        },
      },
      {
        // Above the audience areas and traffic line, below the peak marker.
        name: labels.blocking,
        id: "blocking-period-focus",
        type: "line",
        data: [],
        silent: true,
        z: 6,
        markArea: {
          silent: true,
          z: 6,
          data: focus ? buildFocusVeil(data, colors, options?.activePeriodId) : [],
        },
      },
      ...(estimated
        ? [
            // A casing in the plot colour under the dashes, so the counterfactual stays
            // legible over the areas, the bands and the traffic line it crosses. It
            // shares the series name: the legend shows and hides both.
            {
              name: labels.estimated,
              id: `estimated${CASING_SUFFIX}`,
              type: "line" as const,
              data: estimated,
              showSymbol: false,
              symbol: "none",
              connectNulls: false,
              smooth: false,
              silent: true,
              tooltip: { show: false },
              lineStyle: {
                width: 4,
                color: withAlpha(plotColor, 0.7),
                cap: "round" as const,
                opacity: estimatedOpacity,
              },
              itemStyle: { color: estimatedColor },
              emphasis: { disabled: true },
              z: 6,
            },
            {
              name: labels.estimated,
              id: "estimated",
              type: "line" as const,
              data: estimated,
              showSymbol: false,
              symbol: "none",
              connectNulls: false,
              smooth: false,
              lineStyle: {
                width: 2,
                color: estimatedColor,
                type: [6, 4] as number[],
                cap: "round" as const,
                opacity: estimatedOpacity,
              },
              itemStyle: { color: estimatedColor },
              z: 6,
            },
          ]
        : []),
      ...(gaps.length
        ? [
            {
              // Under everything: the hatch only shows where no audience is drawn.
              name: NO_DATA_LABEL,
              id: "missing-data",
              type: "line" as const,
              data: [],
              silent: true,
              z: 1,
              markArea: { silent: true, z: 1, data: timeNoDataAreas(gaps, colors, span) },
            },
          ]
        : []),
    ],
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const METRIC_LABEL_KEYS = {
  "average-viewers": "summaryAverage",
  "max-viewers": "summaryMax",
  "viewers-disrupted": "summaryDisrupted",
} as const;

/**
 * Figures per row as the widget widens: 2, then 3 from 28rem, 4 from 42rem and
 * all 7 from 56rem. Rows are balanced (`balancedRows`) and each fills the width,
 * so 5 figures read 3 + 2, never 4 + 1, and 7 read 3 + 2 + 2.
 */
const SUMMARY_CAPACITY = { base: 2, md: 3, lg: 4, xl: 7 } as const;
function SummaryStrip({
  summary,
  labels,
  figures,
  metrics,
  position = "top",
}: {
  summary: AudienceSummary;
  labels: AudienceLabels;
  figures: readonly SummaryFigure[];
  metrics: readonly ReportMetric[] | undefined;
  position?: "top" | "left" | "right";
}) {
  const timezone = useDisplayTimezone();
  const { latest, peak, activeInterval } = summary;
  const size = figures.length <= 4 ? "standard" : "compact";
  if (figures.length === 0) return null;
  const layouts = Object.entries(SUMMARY_CAPACITY).map(
    ([size, capacity]) => [size, balancedRows(figures.length, capacity)] as const,
  );
  const stat = (figure: SummaryFigure, style: CSSProperties) => {
    switch (figure.kind) {
      case "latest":
        return (
          <MetricStat
            key={figure.setting}
            size={size}
            style={style}
            label={labels.summaryLatest}
            value={latest ? formatCount(latest.total) : "—"}
            detail={latest ? formatMoment(Date.parse(latest.timestamp), timezone) : undefined}
          />
        );
      case "peak":
        return (
          <MetricStat
            key={figure.setting}
            size={size}
            style={style}
            label={labels.summaryPeak}
            value={peak ? formatCount(peak.total) : "—"}
            detail={peak ? formatMoment(Date.parse(peak.timestamp), timezone) : undefined}
          />
        );
      case "safe":
        return (
          <MetricStat
            key={figure.setting}
            size={size}
            style={style}
            label={labels.summarySafe}
            value={latest?.safeShare != null ? `${Math.round(latest.safeShare * 100)}%` : "—"}
            detail="of latest audience"
          />
        );
      case "blocking":
        return (
          <MetricStat
            key={figure.setting}
            size={size}
            style={style}
            label={labels.summaryBlocking}
            value={activeInterval ? "Active" : "None active"}
            detail={activeInterval?.provider ?? undefined}
          />
        );
      case "metric": {
        const metric = metrics?.find((entry) => entry.id === figure.metricId);
        const value = metric?.value ?? null;
        return (
          <MetricStat
            key={figure.setting}
            size={size}
            style={style}
            label={labels[METRIC_LABEL_KEYS[figure.metricId]]}
            value={value === null ? "—" : formatCount(value)}
            detail={
              metric?.occurredAt ? formatMoment(Date.parse(metric.occurredAt), timezone) : undefined
            }
          />
        );
      }
    }
  };
  return (
    <dl
      style={Object.fromEntries(
        layouts.map(([size, layout]) => [`--summary-columns-${size}`, layout.columns]),
      )}
      className={cn(
        "grid shrink-0 grid-cols-[repeat(var(--summary-columns),minmax(0,1fr))] gap-x-6 gap-y-3 px-(--density-widget-body-padding) pt-2 pb-4",
        "[--summary-columns:var(--summary-columns-base)] @md/widget:[--summary-columns:var(--summary-columns-md)] @2xl/widget:[--summary-columns:var(--summary-columns-lg)]",
        "[&>*]:[--summary-span:var(--summary-span-base)] @md/widget:[&>*]:[--summary-span:var(--summary-span-md)] @2xl/widget:[&>*]:[--summary-span:var(--summary-span-lg)] [&>*]:col-span-(--summary-span)",
        position === "top"
          ? "@4xl/widget:[--summary-columns:var(--summary-columns-xl)] @4xl/widget:[&>*]:[--summary-span:var(--summary-span-xl)]"
          : "@3xl/widget:w-52 @3xl/widget:content-center @3xl/widget:gap-y-8 @3xl/widget:pt-11 @3xl/widget:pb-10 @3xl/widget:[--summary-columns:1] @3xl/widget:[&>*]:[--summary-span:1]",
        position === "right" && "@3xl/widget:order-last",
      )}
    >
      {figures.map((figure, index) =>
        stat(
          figure,
          Object.fromEntries(
            layouts.map(([size, layout]) => [`--summary-span-${size}`, layout.spans[index]]),
          ) as CSSProperties,
        ),
      )}
    </dl>
  );
}

/**
 * Business-impact figures a report snapshot attaches to the series (see
 * `PiracyAudienceReportMetrics`). Shown whenever the data carries them,
 * independent of `showDetails` — unlike `SummaryStrip`'s live diagnostics,
 * these are the primary reason a report reader opens this widget.
 */
function ReportMetricsStrip({
  metrics,
  labels,
}: {
  metrics: PiracyAudienceReportMetrics;
  labels: AudienceLabels;
}) {
  return (
    <dl className="grid shrink-0 grid-cols-1 gap-x-6 gap-y-3 px-(--density-widget-body-padding) pt-(--density-widget-body-padding) @sm/widget:grid-cols-2 @3xl/widget:grid-cols-4">
      <MetricStat
        label={labels.reportAvgEstimated}
        value={formatCount(metrics.avgEstimatedAudience)}
      />
      <MetricStat label={labels.reportAvgApprox} value={formatCount(metrics.avgApproxAudience)} />
      <MetricStat
        label={labels.reportReduction}
        value={`${Math.round(metrics.audienceReductionPct * 100)}%`}
      />
      <MetricStat
        label={labels.reportRevenue}
        value={currencyFormat.format(metrics.revenueOpportunities)}
      />
    </dl>
  );
}

function PiracyAudienceTrendComponent({
  data,
  config,
  presentation,
}: WidgetProps<PiracyAudienceTrendConfig>) {
  const timezone = useDisplayTimezone();
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen: inFocusOverlay } = useContext(WidgetFullscreenContext);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const hasRenderedRef = useRef(false);
  const renderedSelectionRef = useRef<{
    series: PiracyAudienceSeriesData;
    activePeriodId: string | null;
    spotlightEffect: SpotlightEffect;
    showBlocking: boolean;
  } | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [short, setShort] = useState(false);
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean> | undefined>();

  const error = firstError(data, ["series"]);
  const series = unwrap<PiracyAudienceSeriesData>(data.series);
  const summaryInHeader = useHeaderSummaryShown();
  const isLoading = data.series?.isLoading ?? false;
  const title = config.title?.trim() || defaultConfig.title || manifest.name;
  const showDetails = config.showDetails ?? false;
  const summaryFigures = shownSummaryFigures(config);
  const summaryPosition =
    summaryFigures.length <= 3 &&
    (config.summaryPosition === "left" || config.summaryPosition === "right")
      ? config.summaryPosition
      : "top";
  const audienceDisplay = config.audienceDisplay ?? "split";
  const showObserved = config.showObserved ?? true;
  const showEstimated = config.showEstimated ?? true;
  const showBlocking = config.showBlocking ?? true;
  const missingData = missingDataMode(config.missingData);
  const labelsJson = JSON.stringify(resolveLabels(LABELS, config));
  const labels = useMemo(() => JSON.parse(labelsJson) as AudienceLabels, [labelsJson]);

  const summary = useMemo(() => (series ? deriveAudienceSummary(series) : null), [series]);
  const isEmpty = !isLoading && !error && (!series || series.points.length === 0);
  const showChart = !error && !isEmpty && series !== null;
  const firstLoad = isLoading && !hasRenderedRef.current;

  const seriesRef = useRef(series);
  const labelsRef = useRef(labels);
  const activePeriodId = presentation?.blockingPeriodSelection?.activePeriodId ?? null;
  const spotlightEffect: SpotlightEffect = config.spotlightEffect === "band" ? "band" : "focus";
  // The editor canvas never animates; an author can also turn the sweep off.
  const animate = useChartAnimation(config);
  // Annotations and secondary lines wait for the first draw's audience to land.
  const [secondaryShown, setSecondaryShown] = useState(false);
  const settingsRef = useRef<AudienceOptionSettings>({});
  settingsRef.current = {
    timezone,
    labels,
    activePeriodId,
    spotlightEffect,
    animate,
    short,
    audienceDisplay,
    showObserved,
    showEstimated,
    showBlocking,
    missingData,
    legendSelected,
    deferSecondary: !secondaryShown,
  };
  useEffect(() => {
    const host = chartRef.current;
    if (!host || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() =>
      setShort(host.clientHeight > 0 && host.clientHeight < SHORT_CHART_HEIGHT),
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    seriesRef.current = series;
    labelsRef.current = labels;
  }, [series, labels]);

  useEffect(() => {
    const host = chartRef.current;
    if (!host || error || isEmpty || isLoading || !series) return;
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, { renderer: "canvas" });
      setIsZoomed(false);
      chartInstanceRef.current.on("datazoom", (params: unknown) => {
        const p = params as {
          start?: number;
          end?: number;
          batch?: Array<{ start: number; end: number }>;
        };
        const start = p.batch?.[0]?.start ?? p.start ?? 0;
        const end = p.batch?.[0]?.end ?? p.end ?? 100;
        setIsZoomed(start > 0.5 || end < 99.5);
      });
      chartInstanceRef.current.on("legendselectchanged", (params: unknown) => {
        setLegendSelected({ ...(params as { selected: Record<string, boolean> }).selected });
      });
    }
    chartInstanceRef.current.setOption(
      buildOption(series, resolveChartColors(host), { ...settingsRef.current, labels }),
      { replaceMerge: ["series"] },
    );
    renderedSelectionRef.current = { series, activePeriodId, spotlightEffect, showBlocking };
    hasRenderedRef.current = true;
    markContentReady();
  }, [
    series,
    timezone,
    labels,
    short,
    audienceDisplay,
    showObserved,
    showEstimated,
    showBlocking,
    missingData,
    legendSelected,
    secondaryShown,
    error,
    isEmpty,
    isLoading,
    markContentReady,
  ]);

  // Once the first audience has been drawn (its animation finished), the bands and
  // secondary lines follow it. A fallback covers a chart that never reports it.
  const drawn = showChart && !isLoading;
  useEffect(() => {
    if (secondaryShown || !drawn) return;
    const chart = chartInstanceRef.current;
    const reveal = () => setSecondaryShown(true);
    const fallback = setTimeout(reveal, animate ? SECONDARY_REVEAL_MS : 0);
    chart?.on("finished", reveal);
    return () => {
      clearTimeout(fallback);
      if (chart && !chart.isDisposed()) chart.off("finished", reveal);
    };
  }, [secondaryShown, drawn, animate]);

  useEffect(() => {
    const chart = chartInstanceRef.current;
    const host = chartRef.current;
    if (!chart || chart.isDisposed() || !host || !series || !showBlocking) return;
    const rendered = renderedSelectionRef.current;
    if (
      rendered?.series === series &&
      rendered.activePeriodId === activePeriodId &&
      rendered.spotlightEffect === spotlightEffect &&
      rendered.showBlocking === showBlocking
    )
      return;
    const colors = resolveChartColors(host);
    const focus = spotlightEffect === "focus";
    chart.setOption({
      series: [
        {
          id: "blocking-period-bands",
          markArea: { data: buildBlockingBands(series, colors, activePeriodId) },
          markPoint: {
            data: focus ? buildPeakMarker(series, colors, activePeriodId, timezone) : [],
          },
        },
        (() => {
          const tint = buildBlockingTint(series, colors, activePeriodId);
          return {
            id: "blocking-period-tint",
            markArea: { data: tint.areas },
            markLine: { data: tint.edges },
          };
        })(),
        {
          id: "blocking-period-focus",
          markArea: { data: focus ? buildFocusVeil(series, colors, activePeriodId) : [] },
        },
      ],
    });
    renderedSelectionRef.current = { series, activePeriodId, spotlightEffect, showBlocking };
  }, [series, activePeriodId, spotlightEffect, showBlocking, timezone]);

  useChartLifecycle(chartRef, chartInstanceRef, (host, chart) => {
    const current = seriesRef.current;
    if (hasRenderedRef.current && current) {
      chart.setOption(
        buildOption(current, resolveChartColors(host), {
          ...settingsRef.current,
          labels: labelsRef.current,
        }),
        { replaceMerge: ["series"] },
      );
    }
  });

  const freshness = series?.freshness;
  const handleResetZoom = () => {
    chartInstanceRef.current?.dispatchAction({
      type: "dataZoom",
      dataZoomIndex: 0,
      start: 0,
      end: 100,
    });
    setIsZoomed(false);
  };

  const freshnessNote = [
    freshness ? `data through ${formatMoment(Date.parse(freshness.dataThrough), timezone)}` : null,
    freshness?.isEstimated ? "Estimated" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Widget
      title={title}
      intrinsicHeight={!inFocusOverlay}
      headerSummary={
        series?.reportMetrics ? (
          <HeaderStats
            label={`${title} summary`}
            items={[
              {
                label: labels.reportAvgEstimated,
                value: series.reportMetrics.avgEstimatedAudience,
              },
              {
                label: labels.reportReduction,
                value: series.reportMetrics.audienceReductionPct,
                format: "percent",
              },
              { label: labels.reportAvgApprox, value: series.reportMetrics.avgApproxAudience },
              {
                label: labels.reportRevenue,
                value: series.reportMetrics.revenueOpportunities,
                unit: "€",
              },
            ]}
          />
        ) : undefined
      }
      actions={
        <>
          {isLoading && !firstLoad ? <UpdatingPill /> : null}
          {showChart && showDetails && freshness && (
            <InfoTip label="Audience data" side="bottom">
              {freshnessNote}
            </InfoTip>
          )}
          {isZoomed ? (
            <button
              type="button"
              title={labels.resetZoom}
              onClick={handleResetZoom}
              className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-brand-cyan transition-colors hover:bg-surface-elevated hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              {labels.resetZoom}
            </button>
          ) : null}
        </>
      }
    >
      <div
        className={`flex w-full flex-col ${inFocusOverlay ? "h-full min-h-0 overflow-y-auto" : "flex-1"}`}
      >
        {/* Skipped when the header (widget or section) already shows these figures. */}
        {series?.reportMetrics && showChart && !summaryInHeader && (
          <ReportMetricsStrip metrics={series.reportMetrics} labels={labels} />
        )}
        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col",
            summaryPosition !== "top" && "@3xl/widget:flex-row",
          )}
        >
          {summary && showChart && showDetails && (
            <SummaryStrip
              summary={summary}
              labels={labels}
              figures={summaryFigures}
              metrics={series?.metrics}
              position={summaryPosition}
            />
          )}
          <div
            className={`relative min-w-0 flex-1 ${inFocusOverlay ? "min-h-(--widget-chart-min-height)" : "min-h-[var(--widget-chart-fit-min-height,15rem)] @3xl/widget:min-h-[var(--widget-chart-fit-min-height,18rem)]"}`}
            onDoubleClick={handleResetZoom}
          >
            <div
              ref={chartRef}
              role="img"
              aria-label={
                summary
                  ? describeAudienceChart(summary, { audienceDisplay, showObserved, timezone })
                  : "Piracy audience chart"
              }
              className="absolute inset-0 h-full w-full transition-opacity duration-200"
              style={{ opacity: showChart ? 1 : 0 }}
            />
            {error && <WidgetIssueState error={error} className="absolute inset-0" />}
            {isEmpty && (
              <WidgetState
                title={labels.emptyTitle}
                description={labels.emptyDescription}
                className="absolute inset-0"
              />
            )}
            {firstLoad && <WidgetSkeleton shape="chart" className="absolute inset-0" />}
          </div>
        </div>
      </div>
    </Widget>
  );
}

export const Component = PiracyAudienceTrendComponent;

/**
 * The series, and the report figures the summary shows — only those, and none
 * while the summary is off — so the report computes nothing it is not asked for.
 */
export function getDataRequirements(
  config: PiracyAudienceTrendConfig = {},
): Record<string, WidgetDataRequirements> {
  const figures = config.showDetails ? shownSummaryFigures(config) : [];
  const metricIds = summaryMetricIds(figures);
  const showBlocking = config.showBlocking ?? true;
  const showEstimated = config.showEstimated ?? true;
  const needsIntervals =
    showBlocking || showEstimated || figures.some((figure) => figure.kind === "blocking");
  return {
    series: {
      mode: "select",
      ...(metricIds.length ? { metricIds } : {}),
      fields: [
        ...(metricIds.length ? ["metrics"] : []),
        "points",
        "points.safeToBlock",
        "points.unsafeToBlock",
        ...((config.showObserved ?? true) ? ["points.observed"] : []),
        ...(showEstimated ? ["points.estimated"] : []),
        ...(needsIntervals ? ["blockingIntervals"] : []),
        ...(showBlocking
          ? [
              "blockingPeriods.id",
              "blockingPeriods.from",
              "blockingPeriods.to",
              "blockingPeriods.status",
              "blockingApplies",
            ]
          : []),
        "reportMetrics",
        ...(config.showDetails ? ["freshness"] : []),
      ],
    },
  };
}

const widgetModule: WidgetModule<PiracyAudienceTrendConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements,
};

export default widgetModule;
